import { useEffect, useState } from 'react';
import { TASK_STATUS_DEFAULTS, type TaskStatusKey, type TaskStatusRow } from '@ehr/shared';
import { api } from './api.js';

/**
 * ehr#411 - the practice's task status names and colours (Settings → Task Statuses) on every task
 * card, board column and status picker. They were edited there but the cards kept the built-in
 * "Open / In progress / Complete / Cancelled". Read once per page load and shared; the Task
 * Statuses page calls `taskStatusesChanged()` after a save so open screens follow at once.
 */
export interface TaskStatusLook {
  key: TaskStatusKey;
  label: string;
  color: string;
  /** A pale fill of the colour, for pills. */
  bg: string;
}

const DEFAULT_BG: Record<TaskStatusKey, string> = {
  open: '#ecfeff',
  in_progress: '#f3e8ff',
  done: '#edfaf4',
  cancelled: '#f4f7f5',
};

const lookOf = (r: { key: TaskStatusKey; label: string; color: string }): TaskStatusLook => {
  const d = TASK_STATUS_DEFAULTS.find((x) => x.key === r.key);
  // The built-in colour keeps its designed fill; a colour picked in Settings gets a 10% tint.
  const bg =
    d && d.color.toLowerCase() === r.color.toLowerCase() ? DEFAULT_BG[r.key] : `${r.color}1a`;
  return { key: r.key, label: r.label, color: r.color, bg };
};

export const DEFAULT_TASK_STATUSES: TaskStatusLook[] = TASK_STATUS_DEFAULTS.map(lookOf);

let current: TaskStatusLook[] = DEFAULT_TASK_STATUSES;
let loading: Promise<void> | null = null;
const listeners = new Set<(rows: TaskStatusLook[]) => void>();

function load(): Promise<void> {
  loading = api
    .get<TaskStatusRow[]>('/task-statuses')
    .then((rows) => {
      if (!Array.isArray(rows) || !rows.length) return;
      current = [...rows].sort((a, b) => a.position - b.position).map(lookOf);
      for (const fn of listeners) fn(current);
    })
    .catch(() => {
      /* keep the built-in names */
    });
  return loading;
}

/** Re-read after Settings → Task Statuses saves a change. */
export function taskStatusesChanged(): void {
  void load();
}

/** The practice's statuses in their set order (built-in names until the first read lands). */
export function useTaskStatuses(): TaskStatusLook[] {
  const [rows, setRows] = useState(current);
  useEffect(() => {
    listeners.add(setRows);
    if (!loading) void load();
    else setRows(current);
    return () => {
      listeners.delete(setRows);
    };
  }, []);
  return rows;
}

/** One status's look, by key, from a list `useTaskStatuses()` returned. */
export function statusLook(rows: TaskStatusLook[], key: TaskStatusKey): TaskStatusLook {
  return (
    rows.find((r) => r.key === key) ??
    DEFAULT_TASK_STATUSES.find((r) => r.key === key) ??
    DEFAULT_TASK_STATUSES[0]!
  );
}
